'use client';

import type { User } from '@supabase/supabase-js';
import { useEffect } from 'react';

import { Mixpanel } from '@/features/common/analytics/mixpanel';

/**
 * @deprecated Phase 4: 신규 코드는 useUserIdentity 사용 권장
 * Mixpanel SDK의 Simplified ID Merge로 별도 alias 불필요
 * tiktok_sec_uid 우선 정책이 필요한 기존 코드에서만 사용
 *
 * Triggers Mixpanel alias exactly once on the first sign-in of a newly created user
 * in the current browser, then identifies the user. This merges anonymous history
 * with the stable user id and binds subsequent events to that id.
 *
 * Best practice applied:
 * - Only alias on first signup (created_at === last_sign_in_at)
 * - Skip alias if current distinct_id already equals final distinctId
 * - Call identify after alias (중요: alias가 identify보다 먼저 호출되어야 함)
 * - Guard with localStorage to avoid duplicates per browser
 *
 * 🎯 sec_uid 우선 정책:
 * - tiktok_sec_uid가 있으면 sec_uid를 distinct_id로 사용
 * - 없으면 email → user.id 순서로 fallback
 *
 * ⚠️ IMPORTANT: 이 훅은 신규 사용자만 처리하며, Mixpanel identify의 단일 진입점입니다.
 * 기존 사용자는 useIdentifyUserOnLoad에서 처리합니다.
 *
 * @param user The user object from Supabase auth.
 * @param tiktokSecUid The user's TikTok sec_uid from profile (optional).
 */
export function useMixpanelAliasOnFirstSignup(
  user: User | null,
  tiktokSecUid?: string | null,
): void {
  useEffect(() => {
    if (!user) {
      return;
    }

    const createdAtStr = user.created_at;
    const lastSignInStr = user.last_sign_in_at ?? createdAtStr;
    const isNewUser = lastSignInStr === createdAtStr;
    if (!isNewUser) {
      return; // 신규 사용자가 아니면 처리하지 않음
    }

    // 🎯 sec_uid 우선 정책: tiktok_sec_uid → email → user.id
    const finalDistinctId = tiktokSecUid || user.email || user.id;

    const storageKey = `mp_alias_done_${user.id}`;
    try {
      if (typeof window !== 'undefined' && !window.localStorage.getItem(storageKey)) {
        Mixpanel.getDistinctId().then(async currentId => {
          // 🎯 alias는 identify보다 반드시 먼저 호출되어야 함
          // 현재 distinct_id가 finalDistinctId와 다르면 alias 처리
          if (currentId && currentId !== finalDistinctId) {
            await Mixpanel.alias(finalDistinctId);
          }
          // alias 이후 identify 호출
          await Mixpanel.identify(finalDistinctId);
          window.localStorage.setItem(storageKey, '1');
        });
      }
    } catch {
      // ignore storage errors
    }
  }, [user, tiktokSecUid]);
}
